// CTSK-509 — renders a note body as markdown. The stored body is the raw
// markdown source, verbatim; this is purely a view concern.
//
// Safety, in order of how much each layer matters:
//   • raw HTML in a note is never parsed (no rehype-raw): it shows up as
//     literal text, so nothing is lost and nothing executes. The tree is also
//     sanitized (rehype-sanitize, default schema);
//   • images are never loaded — a pasted ![x](https://tracker/pixel) would
//     phone home on every view, which this product does not do. The alt text
//     is shown instead;
//   • links open in a new tab with noopener/noreferrer, and react-markdown's
//     default URL transform already blocks javascript: and similar schemes.

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';

// react-markdown drops raw-HTML nodes (keeping only the text between tags), so
// "<div>x</div>" would lose its tags silently. Turn every html node into plain
// text instead, so what was typed is what is shown and nothing is parsed.
const INLINE_PARENTS = new Set(['paragraph', 'heading', 'emphasis', 'strong', 'delete', 'link', 'tableCell']);
interface MdNode { type: string; value?: string; children?: MdNode[] }
function htmlAsText(node: MdNode): void {
  if (!node.children) return;
  node.children = node.children.map((child) => {
    if (child.type === 'html') {
      const text: MdNode = { type: 'text', value: child.value ?? '' };
      return INLINE_PARENTS.has(node.type) ? text : { type: 'paragraph', children: [text] };
    }
    htmlAsText(child);
    return child;
  });
}
function remarkHtmlAsText() {
  return (tree: MdNode) => htmlAsText(tree);
}

const REMARK = [remarkGfm, remarkHtmlAsText];
const REHYPE = [rehypeSanitize];

const COMPONENTS = {
  a: ({ node: _node, ...props }: Record<string, unknown>) => (
    // eslint-disable-next-line jsx-a11y/anchor-has-content
    <a {...props} target="_blank" rel="noopener noreferrer nofollow" />
  ),
  img: ({ alt }: { alt?: string }) =>
    alt ? <span className="note-md__img-alt">[{alt}]</span> : null,
} as const;

export function NoteMarkdown({ source }: { source: string }) {
  return (
    <div className="note-md" data-testid="note-markdown">
      <ReactMarkdown
        remarkPlugins={REMARK}
        rehypePlugins={REHYPE}
        components={COMPONENTS as never}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
}
